import * as XLSX from 'xlsx';

export const excelParser = {
  /**
   * Parse an Excel file and extract data
   * @param {File} file - Excel file to parse
   * @returns {Promise} Parsed Excel data
   */
  async parse(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            defval: '',
            blankrows: false
          });

          // Build meta map with raw (v) and display (w) for each cell
          const cellMeta = (function buildCellMeta(ws) {
            const meta = {};
            const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
            for (let R = range.s.r; R <= range.e.r; ++R) {
              for (let C = range.s.c; C <= range.e.c; ++C) {
                const addr = XLSX.utils.encode_cell({ r: R, c: C });
                const cell = ws[addr];
                if (cell) {
                  if (!meta[R]) meta[R] = {};
                  meta[R][C] = { v: cell.v, w: cell.w };
                }
              }
            }
            return meta;
          })(worksheet);

          // Helpers for date formatting and parsing
          const formatDateString = (date) => {
            if (!(date instanceof Date) || isNaN(date)) return '';
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const day = date.getDate().toString().padStart(2, '0');
            const year = date.getFullYear();
            return `${day}/${month}/${year}`;
          };

          const parseDateFromString = (str) => {
            if (typeof str !== 'string') return '';
            const s = str.trim();
            const dmy = /^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/;
            const ymd = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/;
            let day, month, year;
            if (dmy.test(s)) {
              const m = s.match(dmy);
              day = parseInt(m[1], 10);
              month = parseInt(m[2], 10);
              year = parseInt(m[3], 10);
              if (year < 100) year += 2000;
              const d = new Date(year, month - 1, day);
              return isNaN(d) ? '' : formatDateString(d);
            }
            if (ymd.test(s)) {
              const m = s.match(ymd);
              year = parseInt(m[1], 10);
              month = parseInt(m[2], 10);
              day = parseInt(m[3], 10);
              const d = new Date(year, month - 1, day);
              return isNaN(d) ? '' : formatDateString(d);
            }
            // NO FALLBACK - if no pattern matches, return empty string
            // This prevents strings like "AVA-INS-001" from being incorrectly parsed as dates
            return '';
          };

          // Convert cell with new rules:
          // - If cell.v !== cell.w => treat as date and cast to dd/mm/yyyy
          // - Else if string looks like a date => cast to dd/mm/yyyy
          const convertExcelValueWithDisplay = (value, rowIndex, colIndex) => {
            const meta = cellMeta[rowIndex] && cellMeta[rowIndex][colIndex] ? cellMeta[rowIndex][colIndex] : null;
            if (meta && meta.w !== undefined && meta.v !== undefined && String(meta.w) !== String(meta.v)) {
              if (typeof meta.v === 'number' && excelParser.isExcelDate(meta.v)) {
                // numeric serial -> date
                const date = excelParser.excelDateToJSDate(meta.v);
                return formatDateString(date);
              }
              // try parsing display string
              const parsed = parseDateFromString(meta.w);
              if (parsed) return parsed;
            }
            if (typeof value === 'string') {
              const parsed = parseDateFromString(value);
              if (parsed) return parsed;
            }
            return value;
          };

          // Extract headers and data
          let rawHeaders = jsonData[0] || [];
          rawHeaders = rawHeaders.map(header =>
            typeof header === 'string' ? header.trim() : String(header ?? '').trim()
          );

          // Filter out empty column headers and track valid indices
          // This prevents key collisions (multiple empty headers all mapping to obj[''])
          // and avoids sending empty field names to the backend schema
          const validColumnIndices = [];
          const headers = [];
          rawHeaders.forEach((header, index) => {
            if (header !== '') {
              validColumnIndices.push(index);
              headers.push(header);
            }
          });

          const rawRows = jsonData.slice(1);

          // Apply display-aware conversion to all cells (only for valid columns)
          const rows = rawRows.map((row, rowIndex) =>
            validColumnIndices.map(colIndex =>
              convertExcelValueWithDisplay(row[colIndex], rowIndex + 1, colIndex)
            )
          );

          // Convert to objects using only valid (non-empty) headers
          const dataObjects = rows.map(row => {
            const obj = {};
            headers.forEach((header, index) => {
              obj[header] = row[index] ?? '';
            });
            return obj;
          });

          resolve({
            headers,
            data: dataObjects,
            rowCount: rows.length,
            columnCount: headers.length,
            sheetNames: workbook.SheetNames
          });
        } catch (error) {
          reject(new Error(`Failed to parse Excel file: ${error.message}`));
        }
      };

      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsArrayBuffer(file);
    });
  },

  /**
   * Get sample data from a column for preview
   * @param {Array} data - Excel data array
   * @param {string} columnName - Column name
   * @param {number} sampleCount - Number of samples to return
   * @returns {string} Sample data joined as string
   */
  getSampleData(data, columnName, sampleCount = 3) {
    return data
      .slice(0, sampleCount)
      .map(row => row[columnName])
      .filter(value => value && String(value).trim())
      .join(', ');
  },

  /**
   * Detect data type of a column based on sample data
   * @param {Array} data - Excel data array
   * @param {string} columnName - Column name
   * @returns {string} Detected data type
   */
  detectDataType(data, columnName) {
    const samples = data
      .slice(0, 10)
      .map(row => row[columnName])
      .filter(Boolean);

    if (samples.length === 0) return 'string';

    // Check for dates
    const datePattern = /^\d{1,2}[/-]\d{1,2}[/-]\d{2,4}$/;
    if (samples.some(sample => datePattern.test(String(sample)))) return 'date';

    // Check for numbers
    if (samples.every(sample => !isNaN(parseFloat(sample)))) return 'number';

    // Check for booleans
    const boolValues = ['true', 'false', 'yes', 'no', '1', '0'];
    if (samples.every(sample => boolValues.includes(String(sample).toLowerCase()))) {
      return 'boolean';
    }

    return 'string';
  },

  /**
   * Validate Excel data structure
   * @param {Object} excelData - Parsed Excel data
   * @returns {Object} Validation result
   */
  validateStructure(excelData) {
    const errors = [];
    const warnings = [];

    // Check if file has data
    if (!excelData.data || excelData.data.length === 0) {
      errors.push('Excel file contains no data rows');
    }

    // Check if file has headers
    if (!excelData.headers || excelData.headers.length === 0) {
      errors.push('Excel file contains no column headers');
    }

    // Check for duplicate headers
    const headerCounts = {};
    excelData.headers.forEach(header => {
      headerCounts[header] = (headerCounts[header] || 0) + 1;
    });

    Object.entries(headerCounts).forEach(([header, count]) => {
      if (count > 1) {
        warnings.push(`Duplicate column header found: "${header}"`);
      }
    });

    // Check for empty headers (should no longer appear after parse() filtering,
    // but kept as defense-in-depth for data passed from external sources)
    const emptyHeaders = excelData.headers.filter(header => !header || String(header).trim() === '');
    if (emptyHeaders.length > 0) {
      warnings.push(`Found ${emptyHeaders.length} empty column header(s) — these columns were excluded`);
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  },

  /**
   * Clean and normalize Excel data
   * @param {Array} data - Raw Excel data
   * @returns {Array} Cleaned data
   */
  cleanData(data) {
    return data.map(row => {
      const cleanRow = {};
      Object.entries(row).forEach(([key, value]) => {
        // Clean header names and skip empty keys
        const cleanKey = String(key).trim();
        if (cleanKey === '') return;

        // Clean values
        let cleanValue = value;
        if (typeof cleanValue === 'string') {
          cleanValue = cleanValue.trim();
        } else if (cleanValue === null || cleanValue === undefined) {
          cleanValue = '';
        }

        cleanRow[cleanKey] = cleanValue;
      });
      return cleanRow;
    });
  },

  /**
   * Convert Excel date serial number to JavaScript date
   * Properly handles Excel's date system and timezone
   * @param {number} serial - Excel date serial number
   * @returns {Date} JavaScript Date object
   */
  excelDateToJSDate(serial) {
    if (serial < 1) {
      return new Date(NaN); // Invalid date
    }

    // Let SheetJS own Excel's 1900 leap-year quirk. Rebuilding the date from
    // calendar components avoids local-time epoch drift across time zones.
    const moduleDefault = Reflect.get(XLSX, 'default');
    const ssf = XLSX.SSF || (moduleDefault && moduleDefault.SSF);
    const parsed = ssf && ssf.parse_date_code(serial);
    if (!parsed) return new Date(NaN);

    return new Date(
      parsed.y,
      parsed.m - 1,
      parsed.d,
      parsed.H,
      parsed.M,
      parsed.S,
      Math.round((parsed.u || 0) * 1000),
    );
  },

  /**
   * Check if a value looks like an Excel date serial number
   * @param {any} value - Value to check
   * @returns {boolean} True if looks like Excel date
   */
  isExcelDate(value) {
    // Excel dates range from 1 (Jan 1, 1900) to 2958465 (Dec 31, 9999)
    // But practically, we'll check for reasonable date ranges
    // 1 = Jan 1, 1900
    // 44926 = Dec 31, 2022
    // 45657 = Dec 31, 2024
    // Let's be generous and allow up to year 2100 (73050)
    return typeof value === 'number' &&
      value >= 1 &&
      value <= 73050; // Reasonable date range up to year 2100
  },

  /**
   * Auto-detect and suggest column mappings
   * @param {Array} headers - Excel column headers
   * @returns {Object} Suggested mappings
   */
  suggestMappings(headers) {
    const mappings = {};
    const commonMappings = {
      // Tag identification
      'tag': 'name',
      'tag no': 'name',
      'tag number': 'name',
      'tag name': 'name',
      'equipment tag': 'name',
      'item tag': 'name',

      // Descriptions
      'description': 'description',
      'equipment description': 'description',
      'tag description': 'description',

      // Facility (do not auto-map facility to avoid requiring mapping)
      // 'facility': 'facilityName',
      // 'facility name': 'facilityName',
      // 'plant': 'facilityName',
      'area': 'area',

      // Common fields
      'manufacturer': 'manufacturer',
      'model': 'model',
      'serial number': 'serialNumber',
      'location': 'location',
      'service': 'service',
      'size': 'size',
      'material': 'material',
      'type': 'type',
      'class': 'class',
      'rating': 'rating'
    };

    headers.forEach(header => {
      const normalizedHeader = String(header).toLowerCase().trim();
      const mapping = commonMappings[normalizedHeader];
      if (mapping) {
        mappings[header] = mapping;
      }
    });

    return mappings;
  }
};
